import { createContext, useContext, useState, useEffect } from 'react';
import { adminTranslations } from './translations/admin';
import { mypageTranslations } from './translations/mypage';
import { pageTranslations } from './translations/pages';
import { mpTranslations } from './translations/mp';
import { contentTranslations } from './translations/content';

// Deep merge helper
const deepMerge = (target, source) => {
  const result = { ...target };
  Object.keys(source).forEach(key => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])
        && target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  });
  return result;
};

// Merge translations helper (deep merge to avoid overwriting nested objects)
const mergeTranslations = (base, ...extensions) => {
  let result = { ...base };
  extensions.forEach(ext => {
    Object.keys(ext).forEach(lang => {
      if (result[lang]) {
        result[lang] = deepMerge(result[lang], ext[lang]);
      } else {
        result[lang] = ext[lang];
      }
    });
  });
  return result;
};

const baseTranslations = {
  ja: {
    nav: {
      home: 'ホーム',
      courses: '講習一覧',
      news: 'お知らせ',
      about: '当財団について',
      contact: 'お問い合わせ',
      apply: '講習申込',
    },
    hero: {
      title: '労働災害の予防には知識が必要です。',
      subtitle: '日本労働者安全衛生推進教育会 一般財団法人',
      description: '技能講習・特別教育を通じて、労働災害の予防と安全な職場環境づくりを支援します',
      cta: '講習を探す',
      ctaSecondary: 'お問い合わせ',
    },
    courses: {
      title: '講習一覧',
      skillTraining: '技能講習',
      specialEducation: '特別教育',
      hybrid: 'ハイブリッド',
      onDemand: 'オンデマンド',
      inPerson: '対面講習',
      days: '日間',
      hours: '時間',
      price: '料金',
      duration: '日数',
      theory: '学科',
      practical: '実技',
      viewDetails: '詳細を見る',
      applyNow: '申込む',
      categories: {
        forklift: 'フォークリフト',
        construction: '建設機械',
        crane: 'クレーン・玉掛け',
        safety: '安全衛生',
        other: 'その他',
      },
    },
    apply: {
      title: '講習申込',
      subtitle: 'ご希望の講習をお選びいただき、必要事項をご入力ください',
      form: {
        name: '氏名',
        nameKana: 'フリガナ',
        email: 'メールアドレス',
        phone: '電話番号',
        company: '会社名',
        course: '希望講習',
        date: '希望日程',
        experience: '関連業務経験',
        experienceYes: 'あり',
        experienceNo: 'なし',
        message: '備考・質問',
        submit: '申込む',
        submitting: '送信中...',
      },
      success: 'お申込みありがとうございます。担当者よりご連絡いたします。',
    },
    contact: {
      title: 'お問い合わせ',
      subtitle: 'ご質問・ご相談がございましたら、お気軽にお問い合わせください',
      form: {
        name: '氏名',
        email: 'メールアドレス',
        phone: '電話番号',
        subject: '件名',
        message: 'お問い合わせ内容',
        submit: '送信する',
        submitting: '送信中...',
      },
      info: {
        address: '所在地',
        phone: '電話番号',
        email: 'メール',
        hours: '営業時間',
      },
      success: 'お問い合わせありがとうございます。担当者よりご連絡いたします。',
    },
    news: {
      title: 'お知らせ',
      readMore: '続きを読む',
      latest: '最新情報',
    },
    newsPage: {
      subtitle: '当財団からの最新のお知らせと更新情報をご覧いただけます',
      announcement: 'お知らせ',
      empty: '現在お知らせはありません',
      backToList: 'お知らせ一覧へ戻る',
      notFound: 'お知らせが見つかりませんでした',
    },
    about: {
      title: '当財団について',
      mission: '理念',
      missionText: '労働災害の予防には知識が必要です。私たちは、安全衛生教育を通じて、すべての労働者が安全に働ける環境づくりを支援します。',
      services: '事業内容',
      features: {
        certified: '公的資格',
        certifiedDesc: '法令に基づいた技能講習・特別教育を実施',
        multilingual: '多言語対応',
        multilingualDesc: '日本語以外の言語でも受講可能',
        practical: '実践重視',
        practicalDesc: '現場で即戦力となる実技訓練を提供',
        support: 'サポート充実',
        supportDesc: '申込から受講まで丁寧にサポート',
      },
    },
    footer: {
      description: '日本労働者安全衛生推進教育会一般財団法人は、労働災害の予防と安全な職場環境づくりを支援する教育機関です。',
      quickLinks: 'クイックリンク',
      contactInfo: '連絡先',
      copyright: '© 2024 JWSEA Foundation. All rights reserved.',
      info: '情報',
      orgName: '日本労働者安全衛生推進教育会 一般財団法人',
      addressRomaji: '〒514-0102 三重県津市栗真町屋町1220-2',
      businessHoursDefault: '平日 9:00 - 18:00',
      recruitment: '採用情報',
      membership: '会員募集',
      reports: '事業報告・決算',
      privacy: '個人情報取扱',
      commercialLaw: '特定商取引法',
      sustainability: 'サステナビリティ',
    },
    common: {
      loading: '読み込み中...',
      error: 'エラーが発生しました',
      selectLanguage: '言語を選択',
      yen: '円',
    },
  },
  en: {
    nav: {
      home: 'Home',
      courses: 'Courses',
      news: 'News',
      about: 'About Us',
      contact: 'Contact',
      apply: 'Apply',
    },
    hero: {
      title: 'Knowledge is Essential for Preventing Workplace Accidents.',
      subtitle: 'Japan Workers Safety and Education Association (JWSEA)',
      description: 'Supporting workplace safety and accident prevention through skill training and special education programs',
      cta: 'Find Courses',
      ctaSecondary: 'Contact Us',
    },
    courses: {
      title: 'Course List',
      skillTraining: 'Skill Training',
      specialEducation: 'Special Education',
      hybrid: 'Hybrid',
      onDemand: 'On-Demand',
      inPerson: 'In-Person',
      days: 'days',
      hours: 'hours',
      price: 'Price',
      duration: 'Duration',
      theory: 'Theory',
      practical: 'Practical',
      viewDetails: 'View Details',
      applyNow: 'Apply Now',
      categories: {
        forklift: 'Forklift',
        construction: 'Construction',
        crane: 'Crane & Slinging',
        safety: 'Safety & Health',
        other: 'Other',
      },
    },
    apply: {
      title: 'Course Application',
      subtitle: 'Select your desired course and fill in the required information',
      form: {
        name: 'Full Name',
        nameKana: 'Name (Phonetic)',
        email: 'Email Address',
        phone: 'Phone Number',
        company: 'Company Name',
        course: 'Desired Course',
        date: 'Preferred Date',
        experience: 'Related Work Experience',
        experienceYes: 'Yes',
        experienceNo: 'No',
        message: 'Notes / Questions',
        submit: 'Submit Application',
        submitting: 'Submitting...',
      },
      success: 'Thank you for your application. We will contact you soon.',
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Feel free to contact us with any questions or inquiries',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        subject: 'Subject',
        message: 'Message',
        submit: 'Send Message',
        submitting: 'Sending...',
      },
      info: {
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        hours: 'Business Hours',
      },
      success: 'Thank you for your message. We will contact you soon.',
    },
    news: {
      title: 'News & Updates',
      readMore: 'Read More',
      latest: 'Latest News',
    },
    newsPage: {
      subtitle: 'Latest news and updates from our foundation',
      announcement: 'Announcement',
      empty: 'There are no news items at this time',
      backToList: 'Back to news list',
      notFound: 'News item not found',
    },
    about: {
      title: 'About Us',
      mission: 'Our Mission',
      missionText: 'Knowledge is essential for preventing workplace accidents. We support the creation of safe working environments through safety and health education.',
      services: 'Our Services',
      features: {
        certified: 'Certified Training',
        certifiedDesc: 'Legally recognized skill training and special education',
        multilingual: 'Multilingual Support',
        multilingualDesc: 'Courses available in multiple languages',
        practical: 'Practical Focus',
        practicalDesc: 'Hands-on training for immediate workplace application',
        support: 'Full Support',
        supportDesc: 'Comprehensive support from application to completion',
      },
    },
    footer: {
      description: 'Japan Workers Safety and Education Association is an educational organization supporting workplace safety and accident prevention.',
      quickLinks: 'Quick Links',
      contactInfo: 'Contact Info',
      copyright: '© 2024 JWSEA Foundation. All rights reserved.',
      info: 'Information',
      orgName: 'Japan Workers Safety & Health Promotion Education Association',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu City, Mie 514-0102, Japan',
      businessHoursDefault: 'Weekdays 9:00 - 18:00',
      recruitment: 'Careers',
      membership: 'Membership',
      reports: 'Business Reports',
      privacy: 'Privacy Policy',
      commercialLaw: 'Commercial Law',
      sustainability: 'Sustainability',
    },
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      selectLanguage: 'Select Language',
      yen: 'JPY',
    },
  },
  pt: {
    nav: {
      home: 'Início',
      courses: 'Cursos',
      news: 'Notícias',
      about: 'Sobre Nós',
      contact: 'Contato',
      apply: 'Inscrição',
    },
    hero: {
      title: 'O conhecimento é essencial para a prevenção de acidentes de trabalho.',
      subtitle: 'Associação Japonesa de Segurança e Educação do Trabalhador (JWSEA)',
      description: 'Apoiando a segurança no trabalho e a prevenção de acidentes através de treinamento de habilidades e programas de educação especial',
      cta: 'Ver Cursos',
      ctaSecondary: 'Contato',
    },
    courses: {
      title: 'Lista de Cursos',
      skillTraining: 'Treinamento de Habilidades',
      specialEducation: 'Educação Especial',
      hybrid: 'Híbrido',
      onDemand: 'Sob Demanda',
      inPerson: 'Presencial',
      days: 'dias',
      hours: 'horas',
      price: 'Preço',
      duration: 'Duração',
      theory: 'Teoria',
      practical: 'Prática',
      viewDetails: 'Ver Detalhes',
      applyNow: 'Inscrever-se',
      categories: {
        forklift: 'Empilhadeira',
        construction: 'Construção',
        crane: 'Guindaste',
        safety: 'Segurança',
        other: 'Outros',
      },
    },
    apply: {
      title: 'Inscrição no Curso',
      subtitle: 'Selecione o curso desejado e preencha as informações necessárias',
      form: {
        name: 'Nome Completo',
        nameKana: 'Nome (Fonético)',
        email: 'E-mail',
        phone: 'Telefone',
        company: 'Empresa',
        course: 'Curso Desejado',
        date: 'Data Preferida',
        experience: 'Experiência Relacionada',
        experienceYes: 'Sim',
        experienceNo: 'Não',
        message: 'Observações',
        submit: 'Enviar Inscrição',
        submitting: 'Enviando...',
      },
      success: 'Obrigado pela sua inscrição. Entraremos em contato em breve.',
    },
    contact: {
      title: 'Contato',
      subtitle: 'Entre em contato conosco para dúvidas ou consultas',
      form: {
        name: 'Nome Completo',
        email: 'E-mail',
        phone: 'Telefone',
        subject: 'Assunto',
        message: 'Mensagem',
        submit: 'Enviar',
        submitting: 'Enviando...',
      },
      info: {
        address: 'Endereço',
        phone: 'Telefone',
        email: 'E-mail',
        hours: 'Horário',
      },
      success: 'Obrigado pela sua mensagem. Entraremos em contato em breve.',
    },
    news: {
      title: 'Notícias',
      readMore: 'Leia Mais',
      latest: 'Últimas Notícias',
    },
    newsPage: {
      subtitle: 'Últimas notícias e atualizações da nossa fundação',
      announcement: 'Aviso',
      empty: 'Não há notícias no momento',
      backToList: 'Voltar para a lista de notícias',
      notFound: 'Notícia não encontrada',
    },
    about: {
      title: 'Sobre Nós',
      mission: 'Nossa Missão',
      missionText: 'O conhecimento é essencial para a prevenção de acidentes de trabalho. Apoiamos a criação de ambientes de trabalho seguros através da educação em segurança e saúde.',
      services: 'Nossos Serviços',
      features: {
        certified: 'Treinamento Certificado',
        certifiedDesc: 'Treinamento de habilidades e educação especial legalmente reconhecidos',
        multilingual: 'Suporte Multilíngue',
        multilingualDesc: 'Cursos disponíveis em vários idiomas',
        practical: 'Foco Prático',
        practicalDesc: 'Treinamento prático para aplicação imediata no trabalho',
        support: 'Suporte Completo',
        supportDesc: 'Suporte abrangente desde a inscrição até a conclusão',
      },
    },
    footer: {
      description: 'A Associação Japonesa de Segurança e Educação do Trabalhador é uma organização educacional que apoia a segurança no trabalho.',
      quickLinks: 'Links Rápidos',
      contactInfo: 'Informações de Contato',
      copyright: '© 2024 JWSEA Foundation. Todos os direitos reservados.',
      info: 'Informações',
      orgName: 'Associação Japonesa de Segurança e Educação do Trabalhador',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu, Mie 514-0102, Japão',
      businessHoursDefault: 'Dias úteis 9:00 - 18:00',
      recruitment: 'Vagas',
      membership: 'Associação',
      reports: 'Relatórios',
      privacy: 'Privacidade',
      commercialLaw: 'Lei Comercial',
      sustainability: 'Sustentabilidade',
    },
    common: {
      loading: 'Carregando...',
      error: 'Ocorreu um erro',
      selectLanguage: 'Selecionar Idioma',
      yen: 'JPY',
    },
  },
  id: {
    nav: {
      home: 'Beranda',
      courses: 'Kursus',
      news: 'Berita',
      about: 'Tentang Kami',
      contact: 'Kontak',
      apply: 'Daftar',
    },
    hero: {
      title: 'Pengetahuan sangat penting untuk mencegah kecelakaan kerja.',
      subtitle: 'Asosiasi Keselamatan dan Pendidikan Pekerja Jepang (JWSEA)',
      description: 'Mendukung keselamatan kerja dan pencegahan kecelakaan melalui pelatihan keterampilan dan program pendidikan khusus',
      cta: 'Lihat Kursus',
      ctaSecondary: 'Hubungi Kami',
    },
    courses: {
      title: 'Daftar Kursus',
      skillTraining: 'Pelatihan Keterampilan',
      specialEducation: 'Pendidikan Khusus',
      hybrid: 'Hibrida',
      onDemand: 'Sesuai Permintaan',
      inPerson: 'Tatap Muka',
      days: 'hari',
      hours: 'jam',
      price: 'Harga',
      duration: 'Durasi',
      theory: 'Teori',
      practical: 'Praktik',
      viewDetails: 'Lihat Detail',
      applyNow: 'Daftar Sekarang',
      categories: {
        forklift: 'Forklift',
        construction: 'Konstruksi',
        crane: 'Derek & Pengangkatan',
        safety: 'Keselamatan',
        other: 'Lainnya',
      },
    },
    apply: {
      title: 'Pendaftaran Kursus',
      subtitle: 'Pilih kursus yang diinginkan dan isi informasi yang diperlukan',
      form: {
        name: 'Nama Lengkap',
        nameKana: 'Nama (Fonetik)',
        email: 'Email',
        phone: 'Telepon',
        company: 'Perusahaan',
        course: 'Kursus yang Diinginkan',
        date: 'Tanggal Pilihan',
        experience: 'Pengalaman Terkait',
        experienceYes: 'Ya',
        experienceNo: 'Tidak',
        message: 'Catatan',
        submit: 'Kirim Pendaftaran',
        submitting: 'Mengirim...',
      },
      success: 'Terima kasih atas pendaftaran Anda. Kami akan menghubungi Anda segera.',
    },
    contact: {
      title: 'Hubungi Kami',
      subtitle: 'Silakan hubungi kami untuk pertanyaan atau konsultasi',
      form: {
        name: 'Nama Lengkap',
        email: 'Email',
        phone: 'Telepon',
        subject: 'Subjek',
        message: 'Pesan',
        submit: 'Kirim',
        submitting: 'Mengirim...',
      },
      info: {
        address: 'Alamat',
        phone: 'Telepon',
        email: 'Email',
        hours: 'Jam Kerja',
      },
      success: 'Terima kasih atas pesan Anda. Kami akan menghubungi Anda segera.',
    },
    news: {
      title: 'Berita',
      readMore: 'Baca Selengkapnya',
      latest: 'Berita Terbaru',
    },
    newsPage: {
      subtitle: 'Berita dan pembaruan terbaru dari yayasan kami',
      announcement: 'Pengumuman',
      empty: 'Tidak ada berita saat ini',
      backToList: 'Kembali ke daftar berita',
      notFound: 'Berita tidak ditemukan',
    },
    about: {
      title: 'Tentang Kami',
      mission: 'Misi Kami',
      missionText: 'Pengetahuan sangat penting untuk mencegah kecelakaan kerja. Kami mendukung penciptaan lingkungan kerja yang aman melalui pendidikan keselamatan dan kesehatan.',
      services: 'Layanan Kami',
      features: {
        certified: 'Pelatihan Bersertifikat',
        certifiedDesc: 'Pelatihan keterampilan dan pendidikan khusus yang diakui secara hukum',
        multilingual: 'Dukungan Multibahasa',
        multilingualDesc: 'Kursus tersedia dalam berbagai bahasa',
        practical: 'Fokus Praktis',
        practicalDesc: 'Pelatihan langsung untuk aplikasi kerja segera',
        support: 'Dukungan Penuh',
        supportDesc: 'Dukungan komprehensif dari pendaftaran hingga penyelesaian',
      },
    },
    footer: {
      description: 'Asosiasi Keselamatan dan Pendidikan Pekerja Jepang adalah organisasi pendidikan yang mendukung keselamatan kerja.',
      quickLinks: 'Tautan Cepat',
      contactInfo: 'Info Kontak',
      copyright: '© 2024 JWSEA Foundation. Hak cipta dilindungi.',
      info: 'Informasi',
      orgName: 'Asosiasi Keselamatan dan Pendidikan Pekerja Jepang',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu, Mie 514-0102, Jepang',
      businessHoursDefault: 'Hari kerja 9:00 - 18:00',
      recruitment: 'Karir',
      membership: 'Keanggotaan',
      reports: 'Laporan',
      privacy: 'Kebijakan Privasi',
      commercialLaw: 'Hukum Komersial',
      sustainability: 'Keberlanjutan',
    },
    common: {
      loading: 'Memuat...',
      error: 'Terjadi kesalahan',
      selectLanguage: 'Pilih Bahasa',
      yen: 'JPY',
    },
  },
  vi: {
    nav: {
      home: 'Trang chủ',
      courses: 'Khóa học',
      news: 'Tin tức',
      about: 'Về chúng tôi',
      contact: 'Liên hệ',
      apply: 'Đăng ký',
    },
    hero: {
      title: 'Kiến thức là điều cần thiết để ngăn ngừa tai nạn lao động.',
      subtitle: 'Hiệp hội An toàn và Giáo dục Người lao động Nhật Bản (JWSEA)',
      description: 'Hỗ trợ an toàn lao động và phòng ngừa tai nạn thông qua đào tạo kỹ năng và chương trình giáo dục đặc biệt',
      cta: 'Xem khóa học',
      ctaSecondary: 'Liên hệ',
    },
    courses: {
      title: 'Danh sách khóa học',
      skillTraining: 'Đào tạo kỹ năng',
      specialEducation: 'Giáo dục đặc biệt',
      hybrid: 'Kết hợp',
      onDemand: 'Theo yêu cầu',
      inPerson: 'Trực tiếp',
      days: 'ngày',
      hours: 'giờ',
      price: 'Giá',
      duration: 'Thời gian',
      theory: 'Lý thuyết',
      practical: 'Thực hành',
      viewDetails: 'Xem chi tiết',
      applyNow: 'Đăng ký ngay',
      categories: {
        forklift: 'Xe nâng',
        construction: 'Xây dựng',
        crane: 'Cần cẩu',
        safety: 'An toàn',
        other: 'Khác',
      },
    },
    apply: {
      title: 'Đăng ký khóa học',
      subtitle: 'Chọn khóa học mong muốn và điền thông tin cần thiết',
      form: {
        name: 'Họ tên',
        nameKana: 'Tên (Phiên âm)',
        email: 'Email',
        phone: 'Điện thoại',
        company: 'Công ty',
        course: 'Khóa học mong muốn',
        date: 'Ngày ưu tiên',
        experience: 'Kinh nghiệm liên quan',
        experienceYes: 'Có',
        experienceNo: 'Không',
        message: 'Ghi chú',
        submit: 'Gửi đăng ký',
        submitting: 'Đang gửi...',
      },
      success: 'Cảm ơn bạn đã đăng ký. Chúng tôi sẽ liên hệ với bạn sớm.',
    },
    contact: {
      title: 'Liên hệ',
      subtitle: 'Vui lòng liên hệ với chúng tôi nếu có câu hỏi',
      form: {
        name: 'Họ tên',
        email: 'Email',
        phone: 'Điện thoại',
        subject: 'Tiêu đề',
        message: 'Nội dung',
        submit: 'Gửi',
        submitting: 'Đang gửi...',
      },
      info: {
        address: 'Địa chỉ',
        phone: 'Điện thoại',
        email: 'Email',
        hours: 'Giờ làm việc',
      },
      success: 'Cảm ơn tin nhắn của bạn. Chúng tôi sẽ liên hệ với bạn sớm.',
    },
    news: {
      title: 'Tin tức',
      readMore: 'Đọc thêm',
      latest: 'Tin mới nhất',
    },
    newsPage: {
      subtitle: 'Tin tức và cập nhật mới nhất từ tổ chức của chúng tôi',
      announcement: 'Thông báo',
      empty: 'Hiện tại không có tin tức nào',
      backToList: 'Quay lại danh sách tin tức',
      notFound: 'Không tìm thấy tin tức',
    },
    about: {
      title: 'Về chúng tôi',
      mission: 'Sứ mệnh',
      missionText: 'Kiến thức là điều cần thiết để ngăn ngừa tai nạn lao động. Chúng tôi hỗ trợ tạo ra môi trường làm việc an toàn thông qua giáo dục an toàn và sức khỏe.',
      services: 'Dịch vụ',
      features: {
        certified: 'Đào tạo có chứng chỉ',
        certifiedDesc: 'Đào tạo kỹ năng và giáo dục đặc biệt được pháp luật công nhận',
        multilingual: 'Hỗ trợ đa ngôn ngữ',
        multilingualDesc: 'Khóa học có sẵn bằng nhiều ngôn ngữ',
        practical: 'Tập trung thực hành',
        practicalDesc: 'Đào tạo thực hành để áp dụng ngay tại nơi làm việc',
        support: 'Hỗ trợ đầy đủ',
        supportDesc: 'Hỗ trợ toàn diện từ đăng ký đến hoàn thành',
      },
    },
    footer: {
      description: 'Hiệp hội An toàn và Giáo dục Người lao động Nhật Bản là tổ chức giáo dục hỗ trợ an toàn lao động.',
      quickLinks: 'Liên kết nhanh',
      contactInfo: 'Thông tin liên hệ',
      copyright: '© 2024 JWSEA Foundation. Mọi quyền được bảo lưu.',
      info: 'Thông tin',
      orgName: 'Hiệp hội An toàn và Giáo dục Người lao động Nhật Bản',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu, Mie 514-0102, Nhật Bản',
      businessHoursDefault: 'Ngày trong tuần 9:00 - 18:00',
      recruitment: 'Tuyển dụng',
      membership: 'Thành viên',
      reports: 'Báo cáo',
      privacy: 'Chính sách bảo mật',
      commercialLaw: 'Luật thương mại',
      sustainability: 'Bền vững',
    },
    common: {
      loading: 'Đang tải...',
      error: 'Đã xảy ra lỗi',
      selectLanguage: 'Chọn ngôn ngữ',
      yen: 'JPY',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      courses: 'Cursos',
      news: 'Noticias',
      about: 'Nosotros',
      contact: 'Contacto',
      apply: 'Inscripción',
    },
    hero: {
      title: 'El conocimiento es esencial para prevenir accidentes laborales.',
      subtitle: 'Asociación Japonesa de Seguridad y Educación del Trabajador (JWSEA)',
      description: 'Apoyando la seguridad laboral y la prevención de accidentes a través de capacitación y programas de educación especial',
      cta: 'Ver Cursos',
      ctaSecondary: 'Contacto',
    },
    courses: {
      title: 'Lista de Cursos',
      skillTraining: 'Capacitación de Habilidades',
      specialEducation: 'Educación Especial',
      hybrid: 'Híbrido',
      onDemand: 'Bajo Demanda',
      inPerson: 'Presencial',
      days: 'días',
      hours: 'horas',
      price: 'Precio',
      duration: 'Duración',
      theory: 'Teoría',
      practical: 'Práctica',
      viewDetails: 'Ver Detalles',
      applyNow: 'Inscribirse',
      categories: {
        forklift: 'Montacargas',
        construction: 'Construcción',
        crane: 'Grúa',
        safety: 'Seguridad',
        other: 'Otros',
      },
    },
    apply: {
      title: 'Inscripción al Curso',
      subtitle: 'Seleccione el curso deseado y complete la información requerida',
      form: {
        name: 'Nombre Completo',
        nameKana: 'Nombre (Fonético)',
        email: 'Correo Electrónico',
        phone: 'Teléfono',
        company: 'Empresa',
        course: 'Curso Deseado',
        date: 'Fecha Preferida',
        experience: 'Experiencia Relacionada',
        experienceYes: 'Sí',
        experienceNo: 'No',
        message: 'Notas',
        submit: 'Enviar Inscripción',
        submitting: 'Enviando...',
      },
      success: 'Gracias por su inscripción. Nos pondremos en contacto pronto.',
    },
    contact: {
      title: 'Contacto',
      subtitle: 'Contáctenos para cualquier pregunta o consulta',
      form: {
        name: 'Nombre Completo',
        email: 'Correo Electrónico',
        phone: 'Teléfono',
        subject: 'Asunto',
        message: 'Mensaje',
        submit: 'Enviar',
        submitting: 'Enviando...',
      },
      info: {
        address: 'Dirección',
        phone: 'Teléfono',
        email: 'Correo',
        hours: 'Horario',
      },
      success: 'Gracias por su mensaje. Nos pondremos en contacto pronto.',
    },
    news: {
      title: 'Noticias',
      readMore: 'Leer Más',
      latest: 'Últimas Noticias',
    },
    newsPage: {
      subtitle: 'Últimas noticias y actualizaciones de nuestra fundación',
      announcement: 'Aviso',
      empty: 'No hay noticias en este momento',
      backToList: 'Volver a la lista de noticias',
      notFound: 'Noticia no encontrada',
    },
    about: {
      title: 'Sobre Nosotros',
      mission: 'Nuestra Misión',
      missionText: 'El conocimiento es esencial para prevenir accidentes laborales. Apoyamos la creación de entornos de trabajo seguros a través de la educación en seguridad y salud.',
      services: 'Nuestros Servicios',
      features: {
        certified: 'Capacitación Certificada',
        certifiedDesc: 'Capacitación de habilidades y educación especial legalmente reconocidas',
        multilingual: 'Soporte Multilingüe',
        multilingualDesc: 'Cursos disponibles en varios idiomas',
        practical: 'Enfoque Práctico',
        practicalDesc: 'Capacitación práctica para aplicación inmediata en el trabajo',
        support: 'Soporte Completo',
        supportDesc: 'Soporte integral desde la inscripción hasta la finalización',
      },
    },
    footer: {
      description: 'La Asociación Japonesa de Seguridad y Educación del Trabajador es una organización educativa que apoya la seguridad laboral.',
      quickLinks: 'Enlaces Rápidos',
      contactInfo: 'Información de Contacto',
      copyright: '© 2024 JWSEA Foundation. Todos los derechos reservados.',
      info: 'Información',
      orgName: 'Asociación Japonesa de Seguridad y Educación del Trabajador',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu, Mie 514-0102, Japón',
      businessHoursDefault: 'Lunes a viernes 9:00 - 18:00',
      recruitment: 'Empleo',
      membership: 'Afiliación',
      reports: 'Informes',
      privacy: 'Privacidad',
      commercialLaw: 'Ley Comercial',
      sustainability: 'Sostenibilidad',
    },
    common: {
      loading: 'Cargando...',
      error: 'Ocurrió un error',
      selectLanguage: 'Seleccionar Idioma',
      yen: 'JPY',
    },
  },
  tl: {
    nav: {
      home: 'Home',
      courses: 'Mga Kurso',
      news: 'Balita',
      about: 'Tungkol sa Amin',
      contact: 'Makipag-ugnayan',
      apply: 'Mag-apply',
    },
    hero: {
      title: 'Ang kaalaman ay mahalaga para sa pag-iwas sa mga aksidente sa trabaho.',
      subtitle: 'Japan Workers Safety and Education Association (JWSEA)',
      description: 'Sinusuportahan ang kaligtasan sa trabaho at pag-iwas sa aksidente sa pamamagitan ng pagsasanay sa kasanayan at mga espesyal na programa ng edukasyon',
      cta: 'Tingnan ang Mga Kurso',
      ctaSecondary: 'Makipag-ugnayan',
    },
    courses: {
      title: 'Listahan ng Kurso',
      skillTraining: 'Pagsasanay sa Kasanayan',
      specialEducation: 'Espesyal na Edukasyon',
      hybrid: 'Hybrid',
      onDemand: 'On-Demand',
      inPerson: 'Personal',
      days: 'araw',
      hours: 'oras',
      price: 'Presyo',
      duration: 'Tagal',
      theory: 'Teorya',
      practical: 'Praktika',
      viewDetails: 'Tingnan ang Detalye',
      applyNow: 'Mag-apply Ngayon',
      categories: {
        forklift: 'Forklift',
        construction: 'Konstruksyon',
        crane: 'Crane',
        safety: 'Kaligtasan',
        other: 'Iba pa',
      },
    },
    apply: {
      title: 'Aplikasyon sa Kurso',
      subtitle: 'Piliin ang iyong nais na kurso at punan ang kinakailangang impormasyon',
      form: {
        name: 'Buong Pangalan',
        nameKana: 'Pangalan (Phonetic)',
        email: 'Email',
        phone: 'Telepono',
        company: 'Kumpanya',
        course: 'Nais na Kurso',
        date: 'Mas Gustong Petsa',
        experience: 'Kaugnay na Karanasan',
        experienceYes: 'Oo',
        experienceNo: 'Hindi',
        message: 'Mga Tala',
        submit: 'Isumite ang Aplikasyon',
        submitting: 'Isinusumite...',
      },
      success: 'Salamat sa iyong aplikasyon. Makikipag-ugnayan kami sa iyo sa lalong madaling panahon.',
    },
    contact: {
      title: 'Makipag-ugnayan',
      subtitle: 'Huwag mag-atubiling makipag-ugnayan sa amin para sa anumang tanong',
      form: {
        name: 'Buong Pangalan',
        email: 'Email',
        phone: 'Telepono',
        subject: 'Paksa',
        message: 'Mensahe',
        submit: 'Ipadala',
        submitting: 'Ipinapadala...',
      },
      info: {
        address: 'Address',
        phone: 'Telepono',
        email: 'Email',
        hours: 'Oras ng Trabaho',
      },
      success: 'Salamat sa iyong mensahe. Makikipag-ugnayan kami sa iyo sa lalong madaling panahon.',
    },
    news: {
      title: 'Balita',
      readMore: 'Magbasa Pa',
      latest: 'Pinakabagong Balita',
    },
    newsPage: {
      subtitle: 'Pinakabagong balita at update mula sa aming pundasyon',
      announcement: 'Anunsyo',
      empty: 'Walang balita sa ngayon',
      backToList: 'Bumalik sa listahan ng balita',
      notFound: 'Hindi natagpuan ang balita',
    },
    about: {
      title: 'Tungkol sa Amin',
      mission: 'Ang Aming Misyon',
      missionText: 'Ang kaalaman ay mahalaga para sa pag-iwas sa mga aksidente sa trabaho. Sinusuportahan namin ang paglikha ng ligtas na mga kapaligiran sa trabaho sa pamamagitan ng edukasyon sa kaligtasan at kalusugan.',
      services: 'Ang Aming mga Serbisyo',
      features: {
        certified: 'Sertipikadong Pagsasanay',
        certifiedDesc: 'Legally recognized skill training and special education',
        multilingual: 'Suporta sa Maraming Wika',
        multilingualDesc: 'Mga kurso na available sa maraming wika',
        practical: 'Praktikal na Pokus',
        practicalDesc: 'Hands-on na pagsasanay para sa agarang aplikasyon sa trabaho',
        support: 'Buong Suporta',
        supportDesc: 'Komprehensibong suporta mula sa aplikasyon hanggang sa pagkumpleto',
      },
    },
    footer: {
      description: 'Ang Japan Workers Safety and Education Association ay isang organisasyong pang-edukasyon na sumusuporta sa kaligtasan sa trabaho.',
      quickLinks: 'Mga Quick Link',
      contactInfo: 'Impormasyon sa Pakikipag-ugnayan',
      copyright: '© 2024 JWSEA Foundation. Lahat ng karapatan ay nakalaan.',
      info: 'Impormasyon',
      orgName: 'Japan Workers Safety and Education Association',
      addressRomaji: '1220-2 Kurimamachiya-cho, Tsu, Mie 514-0102, Japan',
      businessHoursDefault: 'Weekdays 9:00 - 18:00',
      recruitment: 'Mga Trabaho',
      membership: 'Kasapian',
      reports: 'Mga Ulat',
      privacy: 'Patakaran sa Privacy',
      commercialLaw: 'Batas sa Komersyo',
      sustainability: 'Pagpapanatili',
    },
    common: {
      loading: 'Naglo-load...',
      error: 'May naganap na error',
      selectLanguage: 'Pumili ng Wika',
      yen: 'JPY',
    },
  },
  zh: {
    nav: {
      home: '首页',
      courses: '课程一览',
      news: '通知',
      about: '关于本财团',
      contact: '咨询',
      apply: '课程报名',
    },
    hero: {
      title: '预防工伤事故需要知识。',
      subtitle: '日本劳动者安全卫生推进教育会 一般财团法人',
      description: '通过技能讲习与特别教育，协助预防工伤事故、营造安全的工作环境',
      cta: '查找课程',
      ctaSecondary: '咨询',
    },
    courses: {
      title: '课程一览',
      skillTraining: '技能讲习',
      specialEducation: '特别教育',
      hybrid: '混合式',
      onDemand: '点播',
      inPerson: '面授讲习',
      days: '天',
      hours: '小时',
      price: '费用',
      duration: '天数',
      theory: '理论',
      practical: '实操',
      viewDetails: '查看详情',
      applyNow: '报名',
      categories: {
        forklift: '叉车',
        construction: '工程机械',
        crane: '起重机·吊挂',
        safety: '安全卫生',
        other: '其他',
      },
    },
    apply: {
      title: '课程报名',
      subtitle: '请选择您希望的课程并填写所需信息',
      form: {
        name: '姓名',
        nameKana: '假名',
        email: '电子邮箱',
        phone: '电话号码',
        company: '公司名称',
        course: '希望课程',
        date: '希望日期',
        experience: '相关工作经验',
        experienceYes: '有',
        experienceNo: '无',
        message: '备注·问题',
        submit: '报名',
        submitting: '发送中...',
      },
      success: '感谢您的报名。负责人将与您联系。',
    },
    contact: {
      title: '咨询',
      subtitle: '如有任何问题或需要咨询，请随时与我们联系',
      form: {
        name: '姓名',
        email: '电子邮箱',
        phone: '电话号码',
        subject: '主题',
        message: '咨询内容',
        submit: '发送',
        submitting: '发送中...',
      },
      info: {
        address: '所在地',
        phone: '电话号码',
        email: '邮箱',
        hours: '营业时间',
      },
      success: '感谢您的咨询。负责人将与您联系。',
    },
    news: {
      title: '通知',
      readMore: '阅读更多',
      latest: '最新信息',
    },
    newsPage: {
      subtitle: '查看本财团的最新公告和更新信息',
      announcement: '公告',
      empty: '目前没有公告',
      backToList: '返回公告列表',
      notFound: '未找到公告',
    },
    about: {
      title: '关于本财团',
      mission: '理念',
      missionText: '预防工伤事故需要知识。我们通过安全卫生教育，协助营造让所有劳动者都能安全工作的环境。',
      services: '业务内容',
      features: {
        certified: '公共资格',
        certifiedDesc: '依据法令实施技能讲习与特别教育',
        multilingual: '多语言支持',
        multilingualDesc: '可使用日语以外的语言学习',
        practical: '重视实践',
        practicalDesc: '提供在现场即战即用的实操训练',
        support: '完善支持',
        supportDesc: '从报名到学习提供细致的支持',
      },
    },
    footer: {
      description: '日本劳动者安全卫生推进教育会一般财团法人是协助预防工伤事故、营造安全工作环境的教育机构。',
      quickLinks: '快速链接',
      contactInfo: '联系方式',
      copyright: '© 2024 JWSEA Foundation. All rights reserved.',
      info: '信息',
      orgName: '日本劳动者安全卫生推进教育会 一般财团法人',
      addressRomaji: '〒514-0102 三重县津市栗真町屋町1220-2',
      businessHoursDefault: '工作日 9:00 - 18:00',
      recruitment: '招聘信息',
      membership: '会员招募',
      reports: '业务报告·决算',
      privacy: '个人信息处理',
      commercialLaw: '特定商业交易法',
      sustainability: '可持续发展',
    },
    common: {
      loading: '加载中...',
      error: '发生错误',
      selectLanguage: '选择语言',
      yen: '日元',
    },
  },
};

// Merge all translations
const translations = mergeTranslations(baseTranslations, adminTranslations, mypageTranslations, pageTranslations, mpTranslations, contentTranslations);

const languages = [
  { code: 'ja', name: '日本語', flag: '🇯🇵', countryCode: 'jp' },
  { code: 'en', name: 'English', flag: '🇺🇸', countryCode: 'us' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', countryCode: 'br' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', countryCode: 'id' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', countryCode: 'vn' },
  { code: 'es', name: 'Español', flag: '🇪🇸', countryCode: 'es' },
  { code: 'tl', name: 'Tagalog', flag: '🇵🇭', countryCode: 'ph' },
  { code: 'zh', name: '中文', flag: '🇨🇳', countryCode: 'cn' },
];

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('jwsea-language');
    return saved || 'ja';
  });

  useEffect(() => {
    localStorage.setItem('jwsea-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to Japanese
        value = translations['ja'];
        for (const k2 of keys) {
          if (value && value[k2]) {
            value = value[k2];
          } else {
            return key;
          }
        }
        break;
      }
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
